import React, { useEffect, useState } from "react";
import { Search, Plus, Download, Filter } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { getPurchaseList } from "@/redux/Purchase/actions";
import FileUpload from "../FileUpload/FileUpload";
import { set } from "date-fns";
import { DataTable } from "../ui/table";
import { getPurchaseColumns } from "./columns";
import { useDebounced } from "@/hooks/useDebounced";
import { callLoader } from "@/redux/Loader/actions";
import Pagination from "@/pages/PortPlace/Pagination";

const PurchaseOrderList = ({ onCreateNew, onEditOrder }) => {
  const [searchText, setSearchText] = useState(null);
  const [searchField, setSearchField] = useState("ALL");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const userName = authUser?.username;
    const companyId=authUser?.companyId;

  const dispatch = useDispatch();
  const { purchaseList, pagination } = useSelector((state) => state.purchase);

  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const debouncedSearch = useDebounced(() => {
    _getPurchaseList(1);
  }, 300);
  useEffect(() => {
    if (searchText === null) return;
    debouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    _getPurchaseList(1);
  }, [pageSize]);

  useEffect(() => {
    if (selectedOrderId != null) {
      setFileUploadVisible(true);
    } else {
      setFileUploadVisible(false);
    }
  }, [selectedOrderId]);

  const _getPurchaseList = async (page = 1) => {
    setCurrentPage(page);
    const payload = {
      IPageNo: page,
      IPageRecords: pageSize,
      FilterType: searchField,
      SearchKeyword: searchText || "",
      compnayId:companyId
    };
    try {
      dispatch(callLoader(1));
      await dispatch(getPurchaseList(payload));
      setCurrentPage(page);
      dispatch(callLoader(0));
    } catch (error) {
      dispatch(callLoader(0));
    }
  };

  const changePage = (page) => {
    _getPurchaseList(page);
  };
  const handlePageSize = (size) => {
    setPageSize(size);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(orders.map((order) => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const handlers = {
    selectedOrders,
    purchaseList,
    handleSelectAll,
    handleSelectOrder,
    onEditOrder,
    setSelectedOrderId,
  };

  const columns = getPurchaseColumns(handlers);
  return (
    <div className="min-h-full">
      {/* Compact Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
            Purchase Orders
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {pagination?.totalRecord} total orders
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center gap-2">
            <Download className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-medium">Export</span>
          </button>
          <button className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center gap-2">
            <Filter className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-medium">Filter</span>
          </button>
          <button
            onClick={onCreateNew}
            className="flex-1 sm:flex-none px-4 sm:px-5 py-2 bg-[#0072FF] hover:bg-[#0062DD] text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs sm:text-sm">New Order</span>
          </button>
        </div>
      </div>

      {/* Sleek Search Bar - Responsive */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-2 sm:p-3 mb-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="px-3 py-2 text-xs sm:text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
          >
            <option value="all">All Fields</option>
            <option value="order">Order #</option>
            <option value="vendor">Vendor</option>
            <option value="status">Status</option>
          </select>
          <div className="relative flex-1 max-w-[400px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by order number, vendor, or destination..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-xs sm:text-sm border-0 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-[#0072FF] focus:bg-white dark:focus:bg-slate-900 transition-all"
            />
          </div>
        </div>
      </div>

      <DataTable
        data={purchaseList || []}
        columns={columns}
        boxClass="shadow-sm border border-slate-200 dark:border-slate-700"
        trClass="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        thClass="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap uppercase tracking-wider"
        tdClass="text-sm px-2 py-2 border-b border-slate-200 dark:border-slate-700"
        htrClass="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
      />

      <Pagination
        className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-0 bg-slate-50 dark:bg-slate-800/50 mt-8"
        currentPage={currentPage}
        recordsPerPage={pageSize}
        setRecordsPerPage={handlePageSize}
        onPrevious={changePage}
        onNext={changePage}
        totalRecord={pagination?.totalRecord}
        handleGoto={changePage}
        title="purchase Orders"
      />

      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
            <div>
              <FileUpload
                orderId={selectedOrderId?.purchaseOrderId}
                orderNum={selectedOrderId?.poNumber}
                type={"purchase"}
              />
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setFileUploadVisible(false);
                  setSelectedOrderId(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderList;
